import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import Backend from 'i18next-http-backend'

import thTranslation from './locales/th/translation.json'
import enTranslation from './locales/en/translation.json'

const resources = {
  th: {
    translation: thTranslation,
  },
  en: {
    translation: enTranslation,
  },
}

void i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: 'th', // Thai as default language
    fallbackLng: 'th', // Thai as fallback
    debug: import.meta.env.DEV,
    
    detection: {
      order: ['localStorage', 'cookie', 'navigator', 'htmlTag'],
      caches: ['localStorage', 'cookie'],
    },

    react: {
      useSuspense: true,
    },

    // Thai-specific formatting
    defaultNS: 'translation',
    ns: ['translation'],
    
    interpolation: {
      escapeValue: false, // React already escapes values
      // Custom formatting functions
      format: function(value: unknown, format?: string, lng?: string) {
        if (format === 'number' && typeof value === 'number') {
          return new Intl.NumberFormat(lng === 'th' ? 'th-TH' : 'en-US').format(value)
        }
        if (format === 'currency' && typeof value === 'number') {
          return new Intl.NumberFormat(lng === 'th' ? 'th-TH' : 'en-US', {
            style: 'currency',
            currency: 'THB',
          }).format(value)
        }
        if (format === 'buddhistYear' && (value instanceof Date || typeof value === 'string' || typeof value === 'number')) {
          const date = new Date(value)
          const buddhistYear = date.getFullYear() + 543
          if (lng === 'th') {
            return `พ.ศ. ${buddhistYear}`
          }
          return `B.E. ${buddhistYear}`
        }
        if (format === 'date' && (value instanceof Date || typeof value === 'string' || typeof value === 'number')) {
          return new Intl.DateTimeFormat(lng === 'th' ? 'th-TH' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            calendar: lng === 'th' ? 'gregory' : undefined, // Use Gregorian calendar but display Buddhist year
          }).format(new Date(value))
        }
        if (format === 'thaiPhone' && (typeof value === 'string' || typeof value === 'number')) {
          // Format Thai phone number: 081-234-5678
          const cleaned = String(value).replace(/\D/g, '')
          const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
          if (match) {
            return `${match[1]}-${match[2]}-${match[3]}`
          }
        }
        return String(value)
      },
    },
  })

export default i18n

// Thai number converters
export const toThaiNumber = (num: number | string): string => {
  const thaiNumbers = ['๐', '๑', '๒', '๓', '๔', '๕', '๖', '๗', '๘', '๙']
  return num.toString().replace(/\d/g, (digit) => thaiNumbers[parseInt(digit)])
}

export const fromThaiNumber = (thaiNum: string): string => {
  const arabicNumbers: { [key: string]: string } = {
    '๐': '0', '๑': '1', '๒': '2', '๓': '3', '๔': '4',
    '๕': '5', '๖': '6', '๗': '7', '๘': '8', '๙': '9'
  }
  return thaiNum.replace(/[๐-๙]/g, (digit) => arabicNumbers[digit] || digit)
}

// Buddhist year helpers
export const toBuddhistYear = (year: number): number => year + 543
export const fromBuddhistYear = (buddhistYear: number): number => buddhistYear - 543

// Thai month names
export const thaiMonths = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
]

export const thaiMonthsShort = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
]